import { useState } from 'react';
import { useRouter } from 'next/router';

import {
  Accordion,
  Anchor,
  Box,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Label,
  Paragraph,
  Tag,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import { IconAIMagicFilled } from '@zillow/constellation-icons';

import { useAiDraftGeneration } from '../../../../contexts/AiDraftGenerationContext';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { PLEDGE_NOTES_VIOLATION_MESSAGE } from '../../../../hooks/queries/useCheckDiscriminatoryContent';
import { useQrPhotoUploadEntryPoint } from '../../../../hooks/useQrPhotoUploadEntryPoint';
import accessUtils from '../../../../utils/accessUtils';

import { EXTERNAL_URLS } from '../../../../constants/urls';

import AIListingCreationIntroductionModal from '../../../modals/AIListingCreationIntroductionModal';
import AIListingNextConfirmModal from './components/AIListingNextConfirmModal';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import { emitBuildListingManuallyEvent, emitDraftWithAiEvent } from '../../../../analytics/events/modularListing';
import { useAuth } from '../../../AuthProvider';
import HideAddressFormField from '../../../PostingPath/HideAddressFormField';
import PhotoUploadFormField from '../../../PostingPath/MediaContent/PhotoUploadFormField';
import PageWizard from '../../../Wizard/PageWizard';
import { useWizardNavigation } from '../../WizardNavigationProvider';

import type { ChangeEvent } from 'react';
import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const PROPERTY_NOTES_MAX_LENGTH = 1000;
const PROPERTY_NOTES_HARD_LIMIT = PROPERTY_NOTES_MAX_LENGTH * 2;
const PROPERTY_NOTES_OVER_LIMIT_MESSAGE = `Notes must be ${PROPERTY_NOTES_MAX_LENGTH.toLocaleString()} characters or fewer.`;

const AIListingPhotosAndNotes = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  isNextDisabled,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const renderPhoneUpload = useQrPhotoUploadEntryPoint(listingAlias);
  const { user } = useAuth();
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const canAccessFastPost = accessUtils.canAccessFastPost({
    isMultifamily: listingDetails?.isMultifamily ?? false,
    lastActivatedMs: listingDetails?.lastActivatedMs,
    isFeed: listingDetails?.isFeed ?? false,
    restrictions: listingDetails?.restrictions ?? { applications: { isAccessRestricted: false } },
  });

  const [propertyNotes, setPropertyNotes] = useState('');
  const [showIntroModal, setShowIntroModal] = useState(true);
  const closeIntroModal = () => setShowIntroModal(false);

  const handleDraftWithAI = () => {
    closeIntroModal();
    emitDraftWithAiEvent({ currentProperty: listingDetails, user });
  };

  // Opting out skips the draft entirely, so the notes are never sent and no generate call is made.
  const handleBuildManually = () => {
    closeIntroModal();
    emitBuildListingManuallyEvent({ currentProperty: listingDetails, user });
    optOutOfAiListing?.();
  };

  const [showNextConfirmModal, setShowNextConfirmModal] = useState(false);
  const [notesError, setNotesError] = useState<string | undefined>(undefined);

  // The generate call and the write that follows it belong to the wizard, not to this step: the
  // step unmounts as soon as the landlord is moved on, and it would take the callback with it.
  const { checkNotesForPledge, generateAiDraft } = useAiDraftGeneration();
  const { optOutOfAiListing } = useWizardNavigation();

  const notesLength = propertyNotes.length;
  const isNotesOverLimit = notesLength > PROPERTY_NOTES_MAX_LENGTH;
  const notesErrorMessage = notesError ?? (isNotesOverLimit ? PROPERTY_NOTES_OVER_LIMIT_MESSAGE : undefined);
  const notesCounter = (
    <Text
      textStyle="body-xs"
      aria-live="polite"
      css={{ flexShrink: 0, whiteSpace: 'nowrap' }}
      data-testid="AIListingPhotosAndNotes-NotesCounter"
    >
      {notesLength.toLocaleString()}/{PROPERTY_NOTES_MAX_LENGTH.toLocaleString()}
    </Text>
  );

  const handleNotesBlur = async () => {
    if (!propertyNotes.trim() || isNotesOverLimit) return;

    const isClean = await checkNotesForPledge(propertyNotes);
    setNotesError(isClean ? undefined : PLEDGE_NOTES_VIOLATION_MESSAGE);
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep ? () => setShowNextConfirmModal(true) : undefined}
      isNextDisabled={Boolean(isNextDisabled) || !propertyNotes || Boolean(notesErrorMessage)}
      onBackClick={onGoToPrevStep || undefined}
      hideBackButton={true}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <AIListingCreationIntroductionModal
        open={showIntroModal}
        onClose={closeIntroModal}
        onBuildManually={handleBuildManually}
        onDraftWithAI={handleDraftWithAI}
      />
      <AIListingNextConfirmModal
        open={showNextConfirmModal}
        onClose={() => setShowNextConfirmModal(false)}
        onContinue={async () => {
          // Re-checked because the blur result is only as fresh as the last blur. The answer is
          // cached per text, so this is instant for notes that already passed.
          const isClean = await checkNotesForPledge(propertyNotes);
          setShowNextConfirmModal(false);

          if (!isClean) {
            setNotesError(PLEDGE_NOTES_VIOLATION_MESSAGE);
            return;
          }

          generateAiDraft(propertyNotes);
          onGoToNextStep?.();
        }}
      />
      <div data-testid="AIListingPhotosAndNotes">
        <Box css={wizardFlexContainerStyles}>
          <Box css={wizardFormSectionContainerStyles.raw()}>
            <Box css={{ display: 'flex', alignItems: 'center', gap: '400', marginBottom: '400' }}>
              <Heading level={2} textStyle="heading-md">
                Let’s build your listing
              </Heading>
              <Tag tone="purple" icon={<Icon render={<IconAIMagicFilled />} size="sm" />}>
                AI assisted
              </Tag>
            </Box>
            <Paragraph css={wizardParagraphStyles}>
              AI can help build your listing faster by processing your photos and notes to draft a property description
              and suggest amenities. You’ll be asked for details AI can’t determine, like rent and screening criteria.
            </Paragraph>
            <Text textStyle="body-sm-bold" css={{ marginBottom: '100', display: 'block' }}>
              Property photos
            </Text>
            <PhotoUploadFormField emptyStateLayout="panels" renderPhoneUpload={renderPhoneUpload} />
            <Box css={{ marginTop: '800' }}>
              <FormField
                label={<Label>Property notes</Label>}
                required
                error={Boolean(notesErrorMessage)}
                description={
                  <Box css={{ display: 'flex', flexDirection: 'column', gap: '100' }}>
                    <Box
                      css={{
                        display: 'flex',
                        justifyContent: notesErrorMessage ? 'space-between' : 'flex-end',
                        gap: '100',
                      }}
                    >
                      {Boolean(notesErrorMessage) && <FormHelp>{notesErrorMessage}</FormHelp>}
                      {notesCounter}
                    </Box>
                    <FormHelp error={false}>
                      <span>
                        Add details that complement your photos or describe what the photos don’t show, including what
                        makes your property special. Note: Content that may violate Zillow’s{' '}
                        <Anchor href={EXTERNAL_URLS.PLEDGE} target="_blank" rel="noopener noreferrer">
                          Respectful Renting Pledge
                        </Anchor>{' '}
                        will not be included in your description, or will stop the description from being generated.
                      </span>
                    </FormHelp>
                  </Box>
                }
                control={
                  <Textarea
                    value={propertyNotes}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                      setNotesError(undefined);
                      setPropertyNotes(e.target.value);
                    }}
                    onBlur={handleNotesBlur}
                    placeholder="Example: Basement is storage only. Shared driveway with next-door neighbor. No musical instruments."
                    rows={6}
                    maxLength={PROPERTY_NOTES_HARD_LIMIT}
                  />
                }
              />
            </Box>

            {!canAccessFastPost && (
              <Box css={{ marginTop: '800' }}>
                <Text textStyle="body-sm-bold" css={{ marginBottom: '100', display: 'block' }}>
                  Do you want to hide the property address on this listing?
                </Text>

                <HideAddressFormField showPrivacyNote />
              </Box>
            )}

            <Box css={{ marginTop: '800' }}>
              <Accordion.Root>
                <Accordion.Item value="tips">
                  <Accordion.Header>
                    <Heading level={3} css={{ textStyle: 'body-bold' }}>
                      Tips to get best results
                    </Heading>
                  </Accordion.Header>
                  <Accordion.Panel>
                    <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
                      <Box>
                        <Text textStyle="body-sm-bold" css={{ display: 'block' }}>
                          A picture says 1,000 words
                        </Text>
                        <Paragraph>
                          Since we use photos to help draft your property description, include images that feature what
                          makes your place unique.
                        </Paragraph>
                      </Box>
                      <Box>
                        <Text textStyle="body-sm-bold" css={{ display: 'block' }}>
                          Consider what matters to renters
                        </Text>
                        <Paragraph>
                          Use your notes to reference high priority details like location and transit options.
                        </Paragraph>
                      </Box>
                      <Box>
                        <Text textStyle="body-sm-bold" css={{ display: 'block' }}>
                          Make this home stand out
                        </Text>
                        <Paragraph>
                          In both photos and notes, include any upgrades, attractive amenities, and unique details.
                        </Paragraph>
                      </Box>
                    </Box>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion.Root>
            </Box>
            <TextButton tone="brand" onClick={handleBuildManually} css={{ marginTop: '600' }}>
              Build listing manually instead
            </TextButton>
          </Box>
        </Box>
      </div>
    </PageWizard>
  );
};

export default AIListingPhotosAndNotes;
